/**
 * The one CSS `<length-percentage>` parser the PSD export uses. Each relative unit needs a basis
 * the caller supplies; an absent basis rejects that unit rather than guessing one.
 */

/** `rem` is deliberately absent: it resolves against the root font size, which no call site has. */
const LENGTH_RE = /^(-?\d*\.?\d+)(px|%|em)?$/;

export type CssLengthBasis = {
  /** What a `%` resolves against. */
  percent?: number;
  /** What an `em` resolves against. */
  em?: number;
  /** What a unitless number multiplies, as CSS `line-height` does. A bare `0` never needs one. */
  unitless?: number;
};

/** `null` when the value is absent, malformed, or in a unit the caller gave no basis for. */
export function parseCssLength(
  value: string | undefined,
  basis: CssLengthBasis = {},
): number | null {
  const match = value?.trim().toLowerCase().match(LENGTH_RE);
  if (!match?.[1]) return null;
  const number = Number(match[1]);
  if (!Number.isFinite(number)) return null;
  switch (match[2]) {
    case "px":
      return number;
    case "%":
      return basis.percent === undefined
        ? null
        : (number / 100) * basis.percent;
    case "em":
      return basis.em === undefined ? null : number * basis.em;
    default:
      if (number === 0) return 0;
      return basis.unitless === undefined ? null : number * basis.unitless;
  }
}
